/**
 * Error tracking module.
 *
 * Reports errors to Sentry (production) + console (development only) +
 * sessionStorage (a session-scoped ring buffer; nothing in the app reads it today).
 *
 * Console policy, by environment:
 *
 * | environment              | console output                                |
 * |--------------------------|-----------------------------------------------|
 * | development              | full — message + context                      |
 * | production, Sentry on    | nothing (Sentry holds the full record)        |
 * | production, Sentry off   | fixed warning + `component`/`action` only     |
 *
 * The production-with-Sentry row is the one that matters for privacy: `context`
 * carries the stringified React Query key, which embeds the signed-in user's
 * UUID and user-entered filter text. The production-without-Sentry row is the
 * one that matters for observability: it guarantees an error can never vanish
 * without a trace, even if VITE_SENTRY_DSN is ever cleared.
 */
import * as Sentry from '@sentry/react';

interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  [key: string]: unknown;
}

/**
 * Whether errors reported here will actually reach Sentry.
 *
 * Mirrors the `Sentry.init` call in `src/main.tsx`: `enabled: import.meta.env.PROD`
 * with `dsn: import.meta.env.VITE_SENTRY_DSN`. An empty DSN makes Sentry a silent
 * no-op, so both halves have to hold.
 *
 * Read at call time, not module scope, so tests can stub the env (the module-scope
 * read in `logger.ts` is exactly why that module can't be tested this way).
 */
function sentryWillCapture(): boolean {
  return Boolean(import.meta.env.PROD) && Boolean(import.meta.env.VITE_SENTRY_DSN);
}

/** Longest label echoed to the production console. Every real value is well under this. */
const MAX_LABEL_LENGTH = 60;

/**
 * The only fields allowed onto the production console: `component` and `action`.
 *
 * Both are static string literals at every callsite today ('QueryCache',
 * 'MutationCache', 'ErrorBoundary', 'componentDidCatch'). This enforces that
 * contract at runtime rather than trusting it — a future callsite that passes an
 * object, or something user-derived, gets dropped or truncated instead of printed.
 * Undefined entries are omitted so the line stays readable.
 */
function safeLabels(context: ErrorContext): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of ['component', 'action'] as const) {
    const value = context[key];
    if (typeof value === 'string' && value.length > 0) {
      out[key] = value.slice(0, MAX_LABEL_LENGTH);
    }
  }
  return out;
}

interface TrackedError {
  message: string;
  stack?: string;
  context: ErrorContext;
  timestamp: string;
  url: string;
}

const MAX_STORED_ERRORS = 50;
const STORAGE_KEY = 'frankly_error_log';

/**
 * Set the current user on Sentry so errors are associated with their account.
 */
export function identifyUser(userId: string, email?: string): void {
  Sentry.setUser({ id: userId, email: email || undefined });
}

/**
 * Clear user identity (call on logout).
 */
export function clearUser(): void {
  Sentry.setUser(null);
}

/**
 * Coerce arbitrary thrown values into a real Error with a useful message.
 *
 * PostgrestError and similar plain-object errors stringify to "[object Object]"
 * via `String(err)`, losing the actual code/message/details/hint. This helper
 * extracts those fields so Sentry sees a meaningful title instead of
 * "[object Object]". The original payload is preserved on `cause` and passed
 * to Sentry as `extra` so the full shape is available when debugging.
 */
function coerceError(error: unknown): { error: Error; rawFields: Record<string, unknown> } {
  if (error instanceof Error) {
    return { error, rawFields: {} };
  }

  // Plain object — likely a PostgrestError or similar { message, code, details, hint }
  if (error && typeof error === 'object') {
    const obj = error as Record<string, unknown>;
    const message = typeof obj.message === 'string' && obj.message.length > 0
      ? obj.message
      : JSON.stringify(error).slice(0, 200);
    const err = new Error(message);
    return {
      error: err,
      rawFields: {
        code: obj.code,
        details: obj.details,
        hint: obj.hint,
        status: obj.status,
        statusCode: obj.statusCode,
      },
    };
  }

  return { error: new Error(String(error)), rawFields: {} };
}

/**
 * Report an error for tracking. Call this from error boundaries,
 * catch blocks, and mutation onError callbacks.
 */
export function reportError(error: unknown, context: ErrorContext = {}): void {
  const { error: err, rawFields } = coerceError(error);

  const tracked: TrackedError = {
    message: err.message,
    stack: err.stack,
    context,
    timestamp: new Date().toISOString(),
    url: window.location.href,
  };

  if (import.meta.env.DEV) {
    // Development only. `context` is printed verbatim, and it carries the
    // stringified React Query key — which embeds the signed-in user's UUID and
    // record UUIDs (e.g. `['acq_matches', 'paginated', filters, user?.id,
    // activeBrandId]` in useAcqMatchesPaginated) along with user-entered filter
    // text. Vite substitutes `false` for import.meta.env.DEV in a production
    // build, so this branch is dead-code-eliminated from the shipped bundle.
    console.error('[ErrorTracking]', tracked.message, tracked.context);
  } else if (!sentryWillCapture()) {
    // Production with no Sentry DSN. Without this the error would go nowhere:
    // nothing in the app reads getStoredErrors(), so the sessionStorage buffer
    // below is not a support path. Better a thin signal than silence.
    //
    // Only `component` and `action` are emitted. Both are static string literals
    // at every callsite ('QueryCache' / 'MutationCache' / 'ErrorBoundary',
    // 'componentDidCatch'), so neither can carry user data. Everything else is
    // withheld on purpose:
    //   - `message`      — PostgrestError messages quote the offending value, and
    //                      coerceError() falls back to JSON.stringify(error) for
    //                      objects with no message, which can be an entire payload
    //   - `queryKey`     — user UUID + filter text
    //   - `route`        — account / listing / buyer-profile UUIDs
    //   - `componentStack` — noisy, and not needed to know something broke
    // This is withholding, not redaction. Redaction is what you reach for when
    // you don't control the input; here we do.
    console.error(
      '[ErrorTracking] An error was reported but VITE_SENTRY_DSN is not set, ' +
        'so it was not sent anywhere. Details withheld from the console on purpose.',
      safeLabels(context),
    );
  }

  // Report to Sentry with structured fields from the original error preserved
  // as extra context (so PostgrestError code/details/hint aren't lost).
  Sentry.captureException(err, { extra: { ...context, ...rawFields } });

  // Store in sessionStorage for admin debugging
  try {
    const existing: TrackedError[] = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
    existing.push(tracked);
    // Keep only the most recent errors
    if (existing.length > MAX_STORED_ERRORS) {
      existing.splice(0, existing.length - MAX_STORED_ERRORS);
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
  } catch {
    // sessionStorage might be full or unavailable
  }
}

/**
 * Retrieve stored errors (useful for admin debugging panel).
 */
export function getStoredErrors(): TrackedError[] {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}
